import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { memberDirectoryService, Member } from '../../services/memberDirectory';
import { bookingBrandFontFamilyVar, teboxDesignSystem, useBookingTebox } from '../../styles/tebox-booking';

interface MemberSearchProps {
  onMemberSelect: (member: Member) => void;
  onClose: () => void;
  isOpen: boolean;
  placeholder?: string;
  clubNumber?: number;
  brandColors?: {
    fontFamily: string;
    textColor: string;
    secondaryColor: string;
  };
}

export const MemberSearch: React.FC<MemberSearchProps> = ({
  onMemberSelect,
  onClose,
  isOpen,
  placeholder = "Search for a member...",
  clubNumber,
  brandColors: brandColorsProp,
}) => {
  const tebox = useBookingTebox();
  const brandColors = brandColorsProp ?? {
    fontFamily: 'Inter',
    textColor: tebox.colors.textPrimary,
    secondaryColor: tebox.colors.textSecondary
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Debounced search for members
  const [debouncedQuery, setDebouncedQuery] = useState('');
  
  // Debounce the search query
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300); // 300ms delay like the reservation modal

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Search for members when debounced query changes
  const { data: searchResults, isLoading, error } = useQuery({
    queryKey: ['memberSearch', debouncedQuery, clubNumber],
    queryFn: () => {
      console.log('🔍 MemberSearch: Making API call with query:', debouncedQuery, 'clubNumber:', clubNumber);
      return memberDirectoryService.searchMembers(debouncedQuery, 10, clubNumber);
    },
    enabled: debouncedQuery.length >= 1, // Search when query is at least 1 character
    staleTime: 30000, // 30 seconds
  });

  // Debug logging
  useEffect(() => {
    console.log('🔍 MemberSearch: State update', {
      searchQuery,
      debouncedQuery,
      isLoading,
      error,
      membersCount: searchResults?.members?.length || 0
    });
  }, [searchQuery, debouncedQuery, isLoading, error, searchResults]);

  const members = searchResults?.members || [];

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle clicks outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    // Show dropdown immediately when user starts typing
    setIsDropdownOpen(value.length >= 1);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isDropdownOpen || members.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < members.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < members.length) {
          handleMemberSelect(members[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsDropdownOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleMemberSelect = (member: Member) => {
    onMemberSelect(member);
    setSearchQuery('');
    setIsDropdownOpen(false);
    setSelectedIndex(-1);
    onClose();
  };

  const handleInputFocus = () => {
    if (searchQuery.length >= 1) {
      setIsDropdownOpen(true);
    }
  };

  console.log('🔍 MemberSearch render - isOpen:', isOpen);
  
  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: 'var(--brand-border-radius, 6px)',
        padding: '24px',
        width: '90%',
        maxWidth: '500px',
        maxHeight: '80vh',
        overflow: 'hidden',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '20px'
        }}>
          <h3 style={{
            fontFamily: bookingBrandFontFamilyVar,
            fontStyle: 'normal',
            fontWeight: '500',
            fontSize: teboxDesignSystem.typography.h4.fontSize,
            lineHeight: '24px',
            color: brandColors.textColor,
            margin: 0
          }}>
            Add Member
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: teboxDesignSystem.typography.h3.fontSize,
              cursor: 'pointer',
              color: tebox.colors.textSecondary,
              padding: '4px',
              borderRadius: 'var(--brand-border-radius, 6px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = tebox.colors.bgSecondary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            ×
          </button>
        </div>

        {/* Search Input */}
        <div style={{ marginBottom: '20px' }}>
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={(e) => {
              e.target.style.borderColor = '#000000';
              handleInputFocus();
            }}
            placeholder={placeholder}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: `1px solid ${tebox.colors.border}`,
              borderRadius: 'var(--brand-border-radius, 6px)',
              fontSize: teboxDesignSystem.typography.body.fontSize,
              fontFamily: bookingBrandFontFamilyVar,
              outline: 'none',
              boxSizing: 'border-box'
            }}
            onBlur={(e) => {
              e.target.style.borderColor = tebox.colors.border;
            }}
          />
        </div>

        {/* Search Results */}
        <div ref={dropdownRef} style={{ position: 'relative' }}>
          {isLoading && (
            <div style={{
              padding: '20px',
              textAlign: 'left',
              color: brandColors.secondaryColor,
              fontFamily: bookingBrandFontFamilyVar,
              fontSize: teboxDesignSystem.typography.body.fontSize
            }}>
              Searching members...
            </div>
          )}

          {!isLoading && debouncedQuery.length >= 1 && members.length === 0 && (
            <div style={{
              padding: '20px',
              textAlign: 'left',
              color: brandColors.secondaryColor,
              fontFamily: bookingBrandFontFamilyVar,
              fontSize: teboxDesignSystem.typography.body.fontSize
            }}>
              No members found for "{debouncedQuery}"
            </div>
          )}

          {!isLoading && members.length > 0 && (
            <div style={{
              border: `1px solid ${tebox.colors.border}`,
              borderRadius: 'var(--brand-border-radius, 6px)',
              maxHeight: '300px',
              overflowY: 'auto',
              backgroundColor: '#ffffff'
            }}>
              {members.map((member, index) => (
                <div
                  key={member.id}
                  onClick={() => handleMemberSelect(member)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    borderBottom: index < members.length - 1 ? `1px solid ${tebox.colors.border}` : 'none',
                    backgroundColor: selectedIndex === index ? tebox.colors.bgSecondary : 'transparent',
                    transition: 'background-color 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedIndex !== index) {
                      e.currentTarget.style.backgroundColor = tebox.colors.bgHighlight;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedIndex !== index) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <div style={{
                    fontFamily: bookingBrandFontFamilyVar,
                    fontStyle: 'normal',
                    fontWeight: '500',
                    fontSize: teboxDesignSystem.typography.body.fontSize,
                    lineHeight: '20px',
                    color: brandColors.textColor,
                    marginBottom: '2px'
                  }}>
                    {member.firstName} {member.lastName}
                  </div>
                  {member.membershipNumber && (
                    <div style={{
                      fontFamily: bookingBrandFontFamilyVar,
                      fontStyle: 'normal',
                      fontWeight: '400',
                      fontSize: teboxDesignSystem.typography.body.fontSize,
                      lineHeight: '18px',
                      color: brandColors.secondaryColor
                    }}>
                      Member #{member.membershipNumber}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {searchQuery.length < 1 && (
            <div style={{
              padding: '20px',
              textAlign: 'left',
              color: brandColors.secondaryColor,
              fontFamily: bookingBrandFontFamilyVar,
              fontSize: teboxDesignSystem.typography.body.fontSize
            }}>
              Start typing to search for members
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
